import { createFileRoute } from "@tanstack/react-router";
import ArticlePoolResurfacingProcess from "@/pages/ArticlePoolResurfacingProcess";
import { buildHead } from "@/lib/seo";
import { FEATURED_IMAGE_POOL_RESURFACING_PROCESS } from "@/data/blogData";
import { FAQS_POOL_RESURFACING_PROCESS } from "@/data/poolResurfacingProcessData";

const PATH = "/blog/pool-resurfacing-process-south-florida";
const CANONICAL = `https://precisionpaverspools.com${PATH}`;

// BlogPosting schema. The `image` field is included only once a verified
// featured-image URL has been supplied, so the structured data never
// references an empty value.
const blogPosting: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline:
    "Pool Resurfacing Process: What to Expect Before, During, and After",
  description:
    "Understand the South Florida pool resurfacing process, from planning and draining through surface preparation, finish application, refill, startup, and early care.",
  datePublished: "2026-10-20",
  dateModified: "2026-10-20",
  author: {
    "@type": "Organization",
    name: "Precision Pavers & Pool Design Team",
  },
  publisher: {
    "@type": "Organization",
    name: "Precision Pavers & Pool Design",
    url: "https://precisionpaverspools.com/",
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": CANONICAL,
  },
};

if (FEATURED_IMAGE_POOL_RESURFACING_PROCESS) {
  blogPosting.image = FEATURED_IMAGE_POOL_RESURFACING_PROCESS;
}

const jsonLd: Record<string, unknown>[] = [
  blogPosting,
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://precisionpaverspools.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://precisionpaverspools.com/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Pool Resurfacing Process: What to Expect Before, During, and After",
        item: CANONICAL,
      },
    ],
  },
];

// FAQPage schema is emitted only when visible FAQs exist, so the schema
// always exactly matches the on-page FAQ content. During the preparation
// phase the shared FAQ array is empty, so no FAQPage is emitted yet.
if (FAQS_POOL_RESURFACING_PROCESS.length > 0) {
  jsonLd.push({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS_POOL_RESURFACING_PROCESS.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  });
}

export const Route = createFileRoute(
  "/blog/pool-resurfacing-process-south-florida",
)({
  head: () =>
    buildHead({
      title: "Pool Resurfacing Process in South Florida: What to Expect",
      description:
        "Understand the South Florida pool resurfacing process, from planning and draining through surface preparation, finish application, refill, startup, and early care.",
      path: PATH,
      ogType: "article",
      ogImage: FEATURED_IMAGE_POOL_RESURFACING_PROCESS || undefined,
      jsonLd,
    }),
  component: ArticlePoolResurfacingProcess,
});
